import { useMutation } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { ItemVariationList } from '@aeris/contracts'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  Select,
} from '@aeris/ui'

import {
  ApiError,
  createChannelListing,
  fetchItemVariations,
} from '../lib/api.ts'

import { ProductPicker, type PickedProduct } from './product-picker.tsx'

/**
 * Ties one product to one advert on one channel (F030).
 *
 * The product is chosen through `ProductPicker` rather than typed, for the
 * reason F026 built it: this is the field where a mistake ships the wrong item
 * to a stranger, and a free-text SKU is a field where a mistake looks like a
 * success.
 *
 * O código do anúncio é digitado, porque só o marketplace o conhece — e desde
 * a F110 ele é OPCIONAL: em branco, a ligação nasce como rascunho, que é a
 * linha que o Aeris preenche antes de o anúncio existir lá fora.
 *
 * As duas direções do par continuam únicas por canal, e a recusa nomeia o OUTRO
 * lado: quem já ligou este produto é informado de para qual anúncio ele foi, em
 * vez de "já existe".
 *
 * ## A cor e a variação (F177)
 *
 * O produto escolhido é uma COR ("Capa — Azul"). No anúncio próprio da cor
 * (User Products, ou item simples) basta o MLB. No anúncio antigo, com as
 * cores dentro, "Ver variações" lê o anúncio e a pessoa escolhe qual variação
 * é esta cor — a sugestão vem do código, do código de barras ou do nome.
 * "Vincular todas as cores" liga de uma vez cada variação que achou a sua cor
 * e mostra as que sobraram.
 */

export type ChannelListingFormProps = {
  open: boolean
  channelId: string
  onOpenChange: (open: boolean) => void
  onSaved: () => Promise<void> | void
}

export const ChannelListingForm = ({
  open,
  channelId,
  onOpenChange,
  onSaved,
}: ChannelListingFormProps): ReactNode => {
  /* Mounted only while open, so a fresh dialog is a fresh component. */
  const [product, setProduct] = useState<PickedProduct | null>(null)
  const [externalId, setExternalId] = useState('')
  const [externalSku, setExternalSku] = useState('')
  const [error, setError] = useState<string | null>(null)
  /* As variações do anúncio, quando alguém pediu para ver (F177). */
  const [variations, setVariations] = useState<
    ItemVariationList['variations'] | null
  >(null)
  const [variationId, setVariationId] = useState('')
  const [leftOver, setLeftOver] = useState<readonly string[] | null>(null)

  const trimmedExternal = externalId.trim()

  const load = useMutation({
    mutationFn: async () =>
      await fetchItemVariations(
        channelId,
        trimmedExternal,
        product?.productId ?? null
      ),
    onSuccess: (data) => {
      setError(null)
      setVariations(data.variations)
      setLeftOver(null)
      const suggested = data.variations.find(
        (variation) => variation.suggestedVariantId === product?.variantId
      )
      setVariationId(suggested?.id ?? '')
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível ler as variações deste anúncio.'
      )
    },
  })

  const hasVariations = variations !== null && variations.length > 0

  const save = useMutation({
    mutationFn: async () => {
      if (product === null) throw new Error('missing product')
      await createChannelListing(channelId, {
        variantId: product.variantId,
        // Em branco cria um RASCUNHO: o produto entra no canal com preço e
        // prontidão, e o número chega quando o anúncio for criado lá fora.
        externalId: trimmedExternal === '' ? null : trimmedExternal,
        externalVariationId:
          hasVariations && variationId !== '' ? variationId : null,
        externalSku: externalSku.trim() === '' ? null : externalSku.trim(),
        price: null,
        status: 'draft',
      })
    },
    onSuccess: async () => {
      await onSaved()
      onOpenChange(false)
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível ligar este produto.'
      )
    },
  })

  /*
   * Liga cada variação que achou a sua cor e ainda não está ligada. As que
   * sobraram ficam na tela, com o nome, para a pessoa ligar uma a uma.
   */
  const linkAll = useMutation({
    mutationFn: async () => {
      const remaining: string[] = []
      for (const variation of variations ?? []) {
        if (variation.linkedVariantId !== null) continue
        if (variation.suggestedVariantId === null) {
          remaining.push(variation.label)
          continue
        }
        try {
          await createChannelListing(channelId, {
            variantId: variation.suggestedVariantId,
            externalId: trimmedExternal,
            externalVariationId: variation.id,
            externalSku: variation.sellerSku,
            price: null,
            status: 'draft',
          })
        } catch (cause) {
          remaining.push(
            cause instanceof ApiError
              ? `${variation.label}: ${cause.message}`
              : variation.label
          )
        }
      }
      return remaining
    },
    onSuccess: async (remaining) => {
      await onSaved()
      if (remaining.length === 0) {
        onOpenChange(false)
        return
      }
      setLeftOver(remaining)
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Ligar produto a um anúncio"
        description="Um produto tem um anúncio por canal, e um anúncio aponta para um produto só."
        className="max-w-lg"
      >
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            save.mutate()
          }}
        >
          <Field label="Produto" required>
            {() => (
              <ProductPicker
                value={product}
                onChange={(next) => {
                  setProduct(next)
                  // A sugestão era para a cor anterior: ler de novo.
                  setVariations(null)
                  setVariationId('')
                  setLeftOver(null)
                }}
              />
            )}
          </Field>

          <Field
            label="Código do anúncio no canal"
            hint="O que o marketplace chama de anúncio: MLB1234567890, um item_id, um slug. Em branco cria um rascunho, para o anúncio ainda não publicado."
          >
            {(field) => (
              <Input
                {...field}
                value={externalId}
                maxLength={120}
                onChange={(event) => {
                  setExternalId(event.target.value)
                  setVariations(null)
                  setVariationId('')
                  setLeftOver(null)
                }}
              />
            )}
          </Field>

          {trimmedExternal === '' ? null : (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={load.isPending}
                onClick={() => {
                  load.mutate()
                }}
              >
                Ver variações
              </Button>
              {variations !== null && variations.length === 0 ? (
                <span className="text-(length:--text-sm) text-(--text-muted)">
                  Este anúncio não tem variações: a cor se liga a ele inteiro.
                </span>
              ) : null}
            </div>
          )}

          {hasVariations ? (
            <Field
              label="Variação do anúncio"
              hint="Qual variação lá dentro é esta cor. A sugestão vem do código, do código de barras ou do nome da cor."
            >
              {(field) => (
                <Select
                  {...field}
                  value={variationId}
                  onChange={(event) => {
                    setVariationId(event.target.value)
                  }}
                >
                  <option value="">Escolha a variação</option>
                  {variations.map((variation) => (
                    <option
                      key={variation.id}
                      value={variation.id}
                      disabled={variation.linkedVariantId !== null}
                    >
                      {variation.label}
                      {variation.sellerSku === null
                        ? ''
                        : ` · ${variation.sellerSku}`}
                      {variation.linkedVariantId === null ? '' : ' (já ligada)'}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}

          {leftOver === null ? null : (
            <div
              role="status"
              className="text-(length:--text-sm) text-(--text-muted)"
            >
              <p>Estas variações ficaram sem cor — ligue uma a uma:</p>
              <ul className="list-disc pl-5">
                {leftOver.map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            </div>
          )}

          <Field
            label="SKU do vendedor no canal"
            hint="Opcional. Alguns canais guardam um código seu além do deles."
          >
            {(field) => (
              <Input
                {...field}
                value={externalSku}
                maxLength={120}
                onChange={(event) => {
                  setExternalSku(event.target.value)
                }}
              />
            )}
          </Field>

          {error === null ? null : (
            <p role="alert" className="text-(length:--text-sm) text-(--danger)">
              {error}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancelar
              </Button>
            </DialogClose>
            {hasVariations ? (
              <Button
                type="button"
                variant="secondary"
                disabled={linkAll.isPending || product?.productId === undefined}
                onClick={() => {
                  linkAll.mutate()
                }}
              >
                Vincular todas as cores
              </Button>
            ) : null}
            <Button
              type="submit"
              disabled={
                save.isPending ||
                product === null ||
                (hasVariations && variationId === '')
              }
            >
              Ligar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
